import { io, Socket } from 'socket.io-client';

const API_BASE_URL = process.env.REACT_APP_API_BASEURL || 'http://localhost:3000';

/**
 * Socket.IO's `path` option controls the actual HTTP path used for the
 * handshake/polling/upgrade requests (default `/socket.io/`), and it is
 * always resolved relative to the connection origin — NOT to any path
 * segment in the url you pass to `io()`. Passing `io('https://host/mapi')`
 * directly would silently send requests to `https://host/socket.io/...`,
 * skipping the `/mapi` prefix entirely.
 *
 * Since REST calls go through Traefik at `${REACT_APP_API_BASEURL}` (e.g.
 * `https://mapbl.stream/mapi`, stripped to `/` before hitting the backend),
 * the socket must connect to the same origin but with `path` explicitly
 * set to `<prefix>/socket.io/` so Traefik's PathPrefix router picks it up.
 */
const parsed = new URL(API_BASE_URL, window.location.origin);
const SOCKET_ORIGIN = parsed.origin;
const API_PREFIX = parsed.pathname.replace(/\/+$/, ''); // e.g. "/mapi" or ""
const SOCKET_PATH = `${API_PREFIX}/socket.io/`;

let socket: Socket | null = null;

/**
 * Connects (or returns the existing connection) using the current access token.
 * Safe to call multiple times; it will reuse the live socket if one exists.
 */
export function connectSocket(token: string): Socket {
  if (socket && socket.connected) {
    return socket;
  }

  if (socket) {
    socket.disconnect();
  }

  socket = io(SOCKET_ORIGIN, {
    path: SOCKET_PATH,
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  return socket;
}

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Keep Socket.IO's handshake credentials in sync with a refreshed REST access
 * token. A connected socket can continue using its existing session; updating
 * `auth` ensures that any later reconnect is authenticated with the new token.
 */
export function updateSocketAccessToken(token: string): void {
  if (!socket) return;

  socket.auth = { token };

  // A rejected/temporarily disconnected socket may have stopped between
  // reconnection attempts. Start it again with the refreshed credentials.
  if (!socket.connected) {
    socket.connect();
  }
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
